export const PTO_DAILY_CAP_MINUTES = 480;

export function isPtoCode(payCode) {
  return !!payCode && /pto/i.test(payCode);
}

export function isAdminCode(payCode) {
  return !!payCode && /admin|exerc1/i.test(payCode);
}

export function isBirthdayCode(payCode) {
  return !!payCode && /birth/i.test(payCode);
}

export function calculateBlockMinutes(blocks) {
  if (!blocks?.length) return 0;
  return blocks.reduce((sum, block) => {
    if (!block.start_time || !block.end_time) return sum;
    const [sh, sm] = block.start_time.split(':').map(Number);
    const [eh, em] = block.end_time.split(':').map(Number);
    return sum + (eh * 60 + em) - (sh * 60 + sm);
  }, 0);
}

/**
 * Resolve the pay rate for a given reason/pay_code combination.
 * Waterfall: user code rate → user label rate (legacy) → user default → org reason rate → org default → $0
 */
export function resolveRate(reason, payCode, userPayRates, orgTimeTracking) {
  if (payCode && userPayRates?.reason_rates?.[payCode] != null) {
    return Number(userPayRates.reason_rates[payCode]);
  }
  const userReasonRate = userPayRates?.reason_rates?.[reason];
  if (userReasonRate != null) return Number(userReasonRate);
  const userDefault = userPayRates?.default_rate;
  if (userDefault != null) return Number(userDefault);
  const orgReasons = orgTimeTracking?.time_off_reasons || [];
  const orgReason = orgReasons.find(r => r.label === reason);
  if (orgReason?.rate != null) return Number(orgReason.rate);
  if (orgTimeTracking?.default_rate != null) return Number(orgTimeTracking.default_rate);
  return 0;
}

export const EMPLOYMENT_TYPE_LABELS = {
  hourly: 'Hourly',
  salaried: 'Salaried',
  contractor: 'Contractor',
  per_visit: 'Per Visit',
};

export function employmentTypeLabel(type) {
  if (!type) return '';
  return EMPLOYMENT_TYPE_LABELS[type] || (type.charAt(0).toUpperCase() + type.slice(1));
}

/**
 * Strip the "(Recurring)" suffix the calendar appends to recurring block reasons so the
 * label matches the configured reason (same normalization the payroll export applies).
 */
export function normalizeReason(reason) {
  return String(reason || '').replace(/\s*\(Recurring\)\s*$/i, '');
}

/**
 * Resolve the configured pay code for a schedule-override reason label.
 * Only reasons configured in org time_off_reasons count; no default_pay_code fallback.
 */
export function payCodeForReason(reason, orgTimeTracking) {
  const label = normalizeReason(reason);
  const match = (orgTimeTracking?.time_off_reasons || []).find(r => r.label === label);
  return match?.pay_code || '';
}

/**
 * Build the suggested hour allocations for a time entry on a given local date.
 *
 * - Only `unavailable` overrides whose reason resolves to a configured PTO / admin /
 *   birthday pay code are considered (same filter the payroll export applies). Unconfigured
 *   reasons (e.g. personal appointments) are ignored instead of falling back to the default code.
 * - Minutes already allocated to the same reason on OTHER entries for the same local date are
 *   subtracted, so a second or third clock-out that day does not re-allocate the same block.
 *   Rows that net to 0 are dropped.
 *
 * @param {object} args
 * @param {Array} args.overrides - schedule_override rows for that provider/date
 * @param {Array} args.existingEntries - the user's other time entries (any range; filtered to dateStr here)
 * @param {string} args.dateStr - local YYYY-MM-DD
 * @param {object} args.orgTimeTracking - organization.settings.time_tracking
 * @param {object} args.userPayRates - user.pay_rates
 * @param {number|string|null} [args.excludeEntryId] - entry to ignore (e.g. the active one)
 * @returns {Array<{reason:string, minutes:number, pay_code:string, rate:number}>}
 */
export function buildSuggestedAllocations({ overrides, existingEntries, dateStr, orgTimeTracking, userPayRates, excludeEntryId = null }) {
  const tt = orgTimeTracking || {};
  const byReason = {};
  (overrides || []).forEach(o => {
    if (o.type !== 'unavailable' || !o.reason) return;
    const reason = normalizeReason(o.reason);
    const payCode = payCodeForReason(reason, tt);
    if (!payCode) return;
    if (!isPtoCode(payCode) && !isAdminCode(payCode) && !isBirthdayCode(payCode)) return;
    const mins = calculateBlockMinutes(o.blocks);
    if (byReason[reason]) {
      byReason[reason].minutes += mins;
    } else {
      byReason[reason] = {
        reason,
        minutes: mins,
        pay_code: payCode,
        rate: resolveRate(reason, payCode, userPayRates || {}, tt),
      };
    }
  });

  const alreadyByReason = {};
  (existingEntries || []).forEach(e => {
    if (!e || e.status === 'void') return;
    if (excludeEntryId != null && String(e.id) === String(excludeEntryId)) return;
    if (!e.clock_in) return;
    const d = new Date(e.clock_in);
    const pad = (n) => String(n).padStart(2, '0');
    const entryDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (entryDate !== dateStr) return;
    (Array.isArray(e.hour_allocations) ? e.hour_allocations : []).forEach(a => {
      if (!a?.reason) return;
      const ar = normalizeReason(a.reason);
      alreadyByReason[ar] = (alreadyByReason[ar] || 0) + (Number(a.minutes) || 0);
    });
  });

  return Object.values(byReason)
    .map(a => ({ ...a, minutes: Math.max(0, a.minutes - (alreadyByReason[a.reason] || 0)) }))
    .filter(a => a.minutes > 0);
}

/** Local YYYY-MM-DD shifted by `days` from the given local date string. */
export function shiftDateStr(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
